import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Underline } from '@tiptap/extension-underline';
import { Placeholder } from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { TextAlign } from '@tiptap/extension-text-align';
import { FontFamily } from '@tiptap/extension-font-family';
import {
  Bold as IcBold, Italic as IcItalic, Underline as IcUnderline, Strikethrough as IcStrike,
  AlignLeft as IcAlignL, AlignCenter as IcAlignC, AlignRight as IcAlignR,
  List as IcList, ListOrdered as IcListOrdered,
  Link as IcLink, Image as IcImage, Table as IcTable, Paperclip as IcAttach,
  Quote as IcQuote, Code as IcCode, Minus as IcDivider,
  Library as IcSnippets, Sparkles as IcAI,
  Undo as IcUndo, Redo as IcRedo, Eraser as IcEraser, Maximize2 as IcMax, Minimize2 as IcMin,
  Highlighter as IcHighlight, ChevronDown as IcChevron,
} from 'lucide-react';
import ReplyEditor from '../components/ReplyEditor';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const PAGE_SIZE = 50;
const POLL_MS = 45000;

const CLASSIFICATIONS = [
  { value: '', label: 'All' },
  { value: 'reply', label: 'Replies' },
  { value: 'auto_reply', label: 'Auto-replies' },
  { value: 'bounce', label: 'Bounces' },
];

const INTENT_OPTIONS = [
  { value: '',                   label: 'Any intent',         colour: null },
  { value: 'interested',         label: 'Interested',         colour: '#10B981' },
  { value: 'question',           label: 'Question',           colour: '#3B82F6' },
  { value: 'referral',           label: 'Referral',           colour: '#06B6D4' },
  { value: 'out_of_office',      label: 'Out of office',      colour: '#F59E0B' },
  { value: 'not_interested',     label: 'Not interested',     colour: '#F43F5E' },
  { value: 'unsubscribe_request', label: 'Unsubscribe',       colour: '#9F1239' },
  { value: 'other',              label: 'Other',              colour: '#64748B' },
];

const INTENT_META = INTENT_OPTIONS.reduce((acc, o) => { if (o.value) acc[o.value] = o; return acc; }, {});

const OUTCOMES = [
  { value: 'interested', label: 'Interested', colour: '#1a7f3c' },
  { value: 'meeting_booked', label: 'Meeting booked', colour: '#0e7490' },
  { value: 'won', label: 'Won', colour: '#15803d' },
  { value: 'not_interested', label: 'Not interested', colour: '#a16207' },
  { value: 'wrong_person', label: 'Wrong person', colour: '#a16207' },
  { value: 'lost', label: 'Lost', colour: '#b91c1c' },
  { value: 'unsubscribed', label: 'Unsubscribed', colour: '#b91c1c' },
];

const SENTIMENT_COLOURS = {
  positive: '#1a7f3c', neutral: '#666', negative: '#b91c1c',
  objection: '#a16207', unsubscribe_intent: '#b91c1c',
  out_of_office: '#999', wrong_person: '#a16207',
};

function formatWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear
    ? d.toLocaleDateString([], { day: 'numeric', month: 'short' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function senderName(fromEmail) {
  if (!fromEmail) return '';
  const m = fromEmail.match(/^([^<]+?)\s*<.+>$/);
  if (m) return m[1].replace(/^"|"$/g, '').trim();
  return fromEmail.split('@')[0];
}


export default function MailInbox() {
  const ctx = useOutletContext() || {};
  const { clientId, isGlobalMode } = ctx;
  const { getToken } = useAuth();

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [classification, setClassification] = useState('');
  const [intent, setIntent] = useState('');
  const [persona, setPersona] = useState('');
  const [personas, setPersonas] = useState([]);
  const [query, setQuery] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);

  const [replyOpen, setReplyOpen] = useState(false);
  const [replyHtml, setReplyHtml] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState(null);

  const [outcomeSaving, setOutcomeSaving] = useState(false);
  const pollRef = useRef(null);
  const firstLoadRef = useRef(true);

  const buildUrl = useCallback((off) => {
    const params = new URLSearchParams();
    if (classification) params.set('classification', classification);
    if (intent) params.set('intent', intent);
    if (persona) params.set('persona', persona);
    if (query) params.set('q', query);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(off));
    return isGlobalMode
      ? `${API_BASE}/admin/inbox?${params.toString()}`
      : `${API_BASE}/clients/${clientId}/inbox?${params.toString()}`;
  }, [classification, intent, persona, query, isGlobalMode, clientId]);

  const fetchList = useCallback(async ({ append = false, silent = false } = {}) => {
    if (!silent) setListLoading(true);
    setListError(null);
    try {
      const token = await getToken();
      const off = append ? offset : 0;
      const res = await fetch(buildUrl(off), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load inbox (${res.status})`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : (data.rows || []);
      setHasMore(rows.length >= PAGE_SIZE);
      if (append) {
        setList(prev => [...prev, ...rows]);
        setOffset(off + rows.length);
      } else {
        setList(rows);
        setOffset(rows.length);
        if (firstLoadRef.current && rows.length) {
          firstLoadRef.current = false;
          handleSelect(rows[0]);
        }
      }
    } catch (e) {
      if (!silent) setListError(e.message);
    } finally {
      if (!silent) setListLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildUrl, getToken, offset]);

  useEffect(() => {
    if (!isGlobalMode && !clientId) return;
    firstLoadRef.current = true;
    fetchList({ append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalMode, clientId, classification, intent, persona, query]);

  // Load personas for the filter dropdown (client mode only)
  useEffect(() => {
    if (isGlobalMode || !clientId) { setPersonas([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/clients/${clientId}/personas`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok && !cancelled) setPersonas(await res.json());
      } catch (_) { /* leave empty */ }
    })();
    return () => { cancelled = true; };
  }, [isGlobalMode, clientId, getToken]);

  // Background poll for new messages
  useEffect(() => {
    if (!isGlobalMode && !clientId) return;
    pollRef.current = setInterval(() => {
      fetchList({ append: false, silent: true });
    }, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalMode, clientId, classification, intent, persona, query]);

  const fetchThread = useCallback(async (reply) => {
    setThreadLoading(true);
    setThreadError(null);
    setThread(null);
    setReplyOpen(false);
    setReplyHtml('');
    setReplyError(null);
    try {
      const token = await getToken();
      const replyRes = await fetch(`${API_BASE}/inbox/${reply.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!replyRes.ok) throw new Error(`Failed to load reply (${replyRes.status})`);
      const replyData = await replyRes.json();

      let messages = [];
      let contactMeta = null;
      if (replyData.contact_id) {
        const convRes = await fetch(`${API_BASE}/conversations/by-contact/${replyData.contact_id}?mailbox_id=${replyData.mailbox_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (convRes.ok) {
          const convData = await convRes.json();
          messages = convData.messages || [];
          contactMeta = convData.contact || null;
        }
      }
      if (!messages.length) {
        messages = [{
          id: replyData.id, direction: 'inbound', subject: replyData.subject,
          from_email: replyData.from_email, to_email: replyData.to_email,
          occurred_at: replyData.received_at, body_html: replyData.body_html,
          body_text: replyData.body_text, sentiment: replyData.sentiment, intent: replyData.intent,
        }];
      }
      setThread({ reply: replyData, messages, contact: contactMeta });

      if (replyData.is_read === false) {
        fetch(`${API_BASE}/inbox/${reply.id}/read`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_read: true }),
        }).catch(() => {});
        setList(prev => prev.map(r => r.id === reply.id ? { ...r, is_read: true } : r));
      }
    } catch (e) {
      setThreadError(e.message);
    } finally {
      setThreadLoading(false);
    }
  }, [getToken]);

  const handleSelect = (reply) => {
    setSelectedId(reply.id);
    fetchThread(reply);
  };

  const handleSendReply = async () => {
    if (!thread || !replyHtml.replace(/<[^>]+>/g, '').trim()) return;
    setReplySending(true);
    setReplyError(null);
    try {
      const token = await getToken();
      const r = thread.reply;
      const subject = r.subject && r.subject.toLowerCase().startsWith('re:')
        ? r.subject
        : `Re: ${r.subject || ''}`.trim();
      const fromAddr = (r.from_email || '').match(/<([^>]+)>/)?.[1] || r.from_email;
      const res = await fetch(`${API_BASE}/replies/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mailbox_id: r.mailbox_id, to: fromAddr, subject,
          html: replyHtml, text: replyHtml.replace(/<[^>]+>/g, ''),
          in_reply_to_provider_id: r.provider_message_id,
          contact_id: r.contact_id, campaign_id: r.campaign_id,
          original_reply_id: r.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Send failed (${res.status})`);
      setReplyOpen(false);
      setReplyHtml('');
      fetchThread(thread.reply);
    } catch (e) {
      setReplyError(e.message);
    } finally {
      setReplySending(false);
    }
  };

  const handleOutcome = async (outcome) => {
    if (!thread) return;
    setOutcomeSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/inbox/${thread.reply.id}/outcome`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome }),
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      setThread(t => t ? { ...t, reply: { ...t.reply, outcome } } : t);
      setList(prev => prev.map(r => r.id === thread.reply.id ? { ...r, outcome } : r));
    } catch (e) {
      window.alert('Outcome save failed: ' + e.message);
    } finally {
      setOutcomeSaving(false);
    }
  };

  const filteredList = useMemo(() => {
    if (!unreadOnly) return list;
    return list.filter(r => r.is_read === false);
  }, [list, unreadOnly]);

  const unreadCount = useMemo(() => list.filter(r => r.is_read === false).length, [list]);
  const currentOutcome = thread?.reply?.outcome || null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', height: 'calc(100vh - 140px)', gap: 0, border: '1px solid #e5e5e5', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      {/* LEFT — list */}
      <div style={{ borderRight: '1px solid #e5e5e5', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: 14, borderBottom: '1px solid #e5e5e5', background: '#fafafa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
              Inbox {unreadCount > 0 && <span style={{ marginLeft: 6, padding: '1px 7px', background: '#8B1A1A', color: '#fff', fontSize: 11, borderRadius: 10, fontWeight: 600 }}>{unreadCount}</span>}
            </h2>
            <button
              onClick={() => setUnreadOnly(v => !v)}
              style={{ padding: '4px 9px', fontSize: 11, border: '1px solid #d4d4d4', borderRadius: 4, background: unreadOnly ? '#111' : '#fff', color: unreadOnly ? '#fff' : '#111', cursor: 'pointer' }}
            >Unread only</button>
          </div>
          <input
            type="text" placeholder="Search subject, sender, body…"
            value={query} onChange={(e) => setQuery(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d4d4d4', borderRadius: 5, fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }}
          />
          <select
            value={classification} onChange={(e) => setClassification(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d4d4d4', borderRadius: 5, fontSize: 13, background: '#fff', boxSizing: 'border-box', marginBottom: 6 }}
          >
            {CLASSIFICATIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select
            value={intent} onChange={(e) => setIntent(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d4d4d4', borderRadius: 5, fontSize: 13, background: '#fff', boxSizing: 'border-box' }}
          >
            {INTENT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {!isGlobalMode && personas.length > 0 && (
            <select
              value={persona} onChange={(e) => setPersona(e.target.value)}
              style={{ width: '100%', padding: '7px 10px', border: '1px solid #d4d4d4', borderRadius: 5, fontSize: 13, background: '#fff', boxSizing: 'border-box', marginTop: 6 }}
            >
              <option value="">Any persona</option>
              {personas.map(p => <option key={p.id} value={p.id}>{p.display_name}</option>)}
            </select>
          )}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {listLoading && <div style={{ padding: 16, color: '#666', fontSize: 13 }}>Loading…</div>}
          {listError && <div style={{ padding: 16, color: '#b91c1c', fontSize: 13 }}>{listError}</div>}
          {!listLoading && !listError && filteredList.length === 0 && (
            <div style={{ padding: 24, color: '#999', fontSize: 13, textAlign: 'center' }}>No messages.</div>
          )}
          {filteredList.map(r => {
            const isSelected = r.id === selectedId;
            const isUnread = r.is_read === false;
            const outcomeMeta = OUTCOMES.find(o => o.value === r.outcome);
            return (
              <div
                key={r.id} onClick={() => handleSelect(r)}
                style={{
                  padding: 12, borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                  background: isSelected ? '#f5e9e9' : (isUnread ? '#fffdfa' : '#fff'),
                  borderLeft: isSelected ? '3px solid #8B1A1A' : '3px solid transparent',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
                  <div style={{ fontSize: 13, fontWeight: isUnread ? 600 : 500, color: '#111', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {senderName(r.from_email)}
                  </div>
                  <div style={{ fontSize: 11, color: '#888', flexShrink: 0 }}>{formatWhen(r.received_at)}</div>
                </div>
                <div style={{ fontSize: 12, color: '#333', fontWeight: isUnread ? 500 : 400, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.subject || '(no subject)'}
                </div>
                <div style={{ fontSize: 11, color: '#777', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {(r.body_preview || '').replace(/\s+/g, ' ').slice(0, 90)}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                  {r.intent_classification && INTENT_META[r.intent_classification] && (
                    <span style={{
                      fontSize: 10, padding: '1px 6px', borderRadius: 3,
                      background: INTENT_META[r.intent_classification].colour, color: '#fff', fontWeight: 500,
                    }}>
                      {INTENT_META[r.intent_classification].label}
                    </span>
                  )}
                  {r.persona_name && (
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: '#fff', border: '1px solid #8B1A1A', color: '#8B1A1A', fontWeight: 500 }}>
                      {r.persona_name}
                    </span>
                  )}
                  {outcomeMeta && (
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: outcomeMeta.colour, color: '#fff', fontWeight: 500 }}>
                      {outcomeMeta.label}
                    </span>
                  )}
                  {r.sentiment && !outcomeMeta && (
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: '#fff', border: `1px solid ${SENTIMENT_COLOURS[r.sentiment] || '#ccc'}`, color: SENTIMENT_COLOURS[r.sentiment] || '#666' }}>
                      {r.sentiment.replace(/_/g, ' ')}
                    </span>
                  )}
                  {r.company_name && (
                    <span style={{ fontSize: 10, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.company_name}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {hasMore && !listLoading && filteredList.length > 0 && (
            <div style={{ padding: 12, textAlign: 'center' }}>
              <button
                onClick={() => fetchList({ append: true })}
                style={{ padding: '6px 14px', fontSize: 12, border: '1px solid #d4d4d4', borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#333' }}
              >Load more</button>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT — thread + reply */}
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {!thread && !threadLoading && !threadError && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 14 }}>
            Select a message to read.
          </div>
        )}
        {threadLoading && <div style={{ padding: 24, color: '#666' }}>Loading thread…</div>}
        {threadError && <div style={{ padding: 24, color: '#b91c1c' }}>{threadError}</div>}
        {thread && (
          <>
            <div style={{ padding: 16, borderBottom: '1px solid #e5e5e5', background: '#fafafa' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 4 }}>
                    {thread.reply.subject || '(no subject)'}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {thread.contact?.full_name || senderName(thread.reply.from_email)}
                    {thread.contact?.job_title && <span> · {thread.contact.job_title}</span>}
                    {thread.reply.company_name && <span> · {thread.reply.company_name}</span>}
                    {thread.reply.campaign_name && <span> · Campaign: {thread.reply.campaign_name}</span>}
                  </div>
                </div>
                <div style={{ flexShrink: 0 }}>
                  <select
                    value={currentOutcome || ''}
                    disabled={outcomeSaving}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '__clear__') { handleOutcome(null); return; }
                      if (v === '') return;
                      handleOutcome(v);
                    }}
                    style={{ padding: '6px 10px', fontSize: 12, border: '1px solid #d4d4d4', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
                  >
                    <option value="" disabled hidden>Set outcome…</option>
                    {OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    {currentOutcome && <option value="__clear__">— Clear outcome —</option>}
                  </select>
                </div>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 16, minHeight: 0 }}>
              {thread.messages.map((m, i) => {
                const isInbound = m.direction === 'inbound';
                return (
                  <div key={`${m.direction}-${m.id}-${i}`} style={{
                    marginBottom: 14, padding: 14,
                    background: isInbound ? '#fff' : '#f8f8f8',
                    border: '1px solid #e5e5e5',
                    borderLeft: `3px solid ${isInbound ? '#8B1A1A' : '#666'}`,
                    borderRadius: 4,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 12, color: '#666' }}>
                      <div>
                        <strong style={{ color: '#111' }}>{isInbound ? 'From' : 'Sent'}:</strong>{' '}
                        {isInbound ? m.from_email : m.to_email}
                      </div>
                      <div>{m.occurred_at ? new Date(m.occurred_at).toLocaleString() : ''}</div>
                    </div>
                    {m.body_html
                      ? <div style={{ fontSize: 13, color: '#222', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: m.body_html }} />
                      : <div style={{ fontSize: 13, color: '#222', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{m.body_text || ''}</div>
                    }
                  </div>
                );
              })}
            </div>
            <div style={{ borderTop: '1px solid #e5e5e5', padding: 14, background: '#fafafa' }}>
              {!replyOpen ? (
                <button
                  onClick={() => setReplyOpen(true)}
                  style={{ padding: '8px 16px', background: '#8B1A1A', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
                >Reply</button>
              ) : (
                <div>
                  <ReplyEditor value={replyHtml} onChange={setReplyHtml} clientId={clientId} />
                  {replyError && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 8 }}>{replyError}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button
                      onClick={handleSendReply}
                      disabled={replySending || !replyHtml.replace(/<[^>]+>/g, '').trim()}
                      style={{ padding: '8px 16px', background: replySending || !replyHtml.replace(/<[^>]+>/g, '').trim() ? '#ccc' : '#8B1A1A', color: '#fff', border: 'none', borderRadius: 5, cursor: replySending || !replyHtml.replace(/<[^>]+>/g, '').trim() ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500 }}
                    >{replySending ? 'Sending…' : 'Send'}</button>
                    <button
                      onClick={() => { setReplyOpen(false); setReplyHtml(''); setReplyError(null); }}
                      disabled={replySending}
                      style={{ padding: '8px 16px', background: '#fff', color: '#333', border: '1px solid #d4d4d4', borderRadius: 5, cursor: 'pointer', fontSize: 13 }}
                    >Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
